import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Decides whether a screen is being entered "cold" — browser refresh, direct URL, or landing after
 * login — as opposed to in-app navigation, so it can show a full-page loader instead of section
 * skeletons. Used by the Collection lobby; any other module can opt in with the same two calls.
 *
 *   const showFullPageLoader = navigationContext.claimColdLoad('collection');
 *   ... once the loader finishes: navigationContext.releaseColdLoad('collection');
 *
 * Two things rule out the obvious "has any navigation completed yet?" test, both observed in this
 * app: a redirect (`/login`, a guard bounce) completes a navigation BEFORE the target screen is
 * activated, and a screen can re-navigate to itself (Collection appending its subEntityId), which
 * destroys and recreates the component mid-load. Tracking the module that rendered — and latching
 * the loader only once it has actually finished — ignores both.
 *
 * AppComponent feeds this on every NavigationEnd; it must stay injected there so tracking starts at
 * bootstrap rather than whenever a feature screen first asks. State lives for one document load.
 */
/**
 * Screens that own a cold-load loader of their own even though they sit under another module's
 * path (`/first-party/:id/collection/smartmapped` and friends). Without these, every collection
 * sub-screen would share the lobby's key and inherit its already-released cold load.
 */
export const COLD_LOAD_SCREEN_KEYS = {
  SMART_MAPPING: 'smartmapped',
  ARTIFACTS_REGISTRY: 'artifacts-registry',
  AI_TASKS: 'ai-tasks',
  // `/first-party/:id/collection/tear-sheet` - without its own key it would share 'collection'
  // with the lobby and inherit its already-released cold load
  TEAR_SHEET: 'tear-sheet',
  // `/first-party/:id/pillars/graphs` - without its own key it would share 'pillars' with the
  // pillars screen, so entering one would consume the other's cold load
  REPORTING_GRAPHS: 'graphs',
  // `/clients/user-settings` and `/first-party/:id/user-settings` - without its own key the former
  // would read as 'clients' and inherit Management's already-released cold load
  USER_SETTINGS: 'user-settings',
};

/** Module-level keys (the first meaningful URL segment) for screens that opt into the cold loader. */
export const COLD_LOAD_MODULE_KEYS = {
  MULTI_ENTITY: 'multi-entity',
  REGULATORY_WATCH: 'regulatory-watch',
  POLICIES: 'policies',
  UPPERDECK: 'upperdeck',
  CLIENTS: 'clients',
  BREAK_DOWN: 'break-down',
  // `/third-party/:entityId/overview` - moduleKeyOf() takes the third segment under a scoped path,
  // so this screen is keyed 'overview' and does not share the shell's key with its sibling tabs
  THIRD_PARTY_OVERVIEW: 'overview',
  // `/third-party/:entityId/heat-map`, keyed by the same third-segment rule as the overview
  THIRD_PARTY_HEAT_MAP: 'heat-map',
  // `/third-party/:entityId/default-settings`, same third-segment rule again
  THIRD_PARTY_DEFAULT_SETTINGS: 'default-settings',
  // `/third-party/:entityId/vendors`, same third-segment rule again
  THIRD_PARTY_VENDORS: 'vendors',
  // `/settings` is Users Settings' own top-level mount; its other two mounts key as
  // 'user-settings' (COLD_LOAD_SCREEN_KEYS), so the screen claims whichever matches its URL
  SETTINGS: 'settings',
  // Remediation is mounted three times and reads as two keys: `/first-party/:entityId/remediation`,
  // and `/third-party/:entityId/gaps` + `/vendor/:entityId/gaps` which both key as 'gaps'. The
  // screen claims whichever matches its own URL, the same way Users Settings does
  REMEDIATION: 'remediation',
  GAPS: 'gaps',
  // `/first-party/:entityId/pillars[/:subEntityId]`, keyed by the same third-segment rule
  PILLARS: 'pillars',
  // Risk Register is mounted twice and reads as two keys: `/first-party/:entityId/risk-register`
  // (plus its `/:subEntityId`, `/archive` and `/groups` children, which keep the same third
  // segment) and `/first-party/:entityId/risk-map`. One component serves both, so it claims
  // whichever matches its own URL, the same way Remediation does
  RISK_REGISTER: 'risk-register',
  RISK_MAP: 'risk-map',
};

@Injectable({ providedIn: 'root' })
export class NavigationContextService {
  private readonly modulesRendered = new Set<string>();
  private readonly coldLoadReleased = new Set<string>();

  // keys whose full-page loader is on screen right now. The index.html splash reads this so a
  // refresh shows ONE loader end to end: it holds instead of handing over to the screen's own
  // loader, which sits invisible underneath it until both end together.
  private readonly activeColdLoads = new Set<string>();
  private readonly coldLoadIdleSubject = new Subject<void>();

  /** Emits when the last active cold load ends, i.e. the screen's data has landed. */
  get coldLoadIdle$(): Observable<void> {
    return this.coldLoadIdleSubject.asObservable();
  }

  /** True while any screen's cold-load full-page loader is showing. */
  hasActiveColdLoad(): boolean {
    return this.activeColdLoads.size > 0;
  }

  // routes that are not feature modules: landing on a module straight after one of these is still
  // a cold entry from the user's point of view
  private static readonly NON_MODULE_ROUTES = ['login', 'signup', 'not-found', 'not-authorized', 'onboarding'];

  // a screen from the list above is keyed by its own name wherever it is mounted
  private static readonly SCREEN_ROUTES = Object.values(COLD_LOAD_SCREEN_KEYS);

  markNavigationEnd(url: string): void {
    const moduleKey = NavigationContextService.moduleKeyOf(url);
    if (moduleKey) {
      this.modulesRendered.add(moduleKey);
    }
    // A redirect or guard bounce destroys the claiming component without it ever releasing, so any
    // claim for a key we have now navigated away from is stale and must not hold the splash up.
    this.activeColdLoads.forEach(key => {
      if (key !== moduleKey) {
        this.activeColdLoads.delete(key);
      }
    });
    this.emitIfIdle();
  }

  /**
   * True when `moduleKey` is being entered cold: no other feature module has rendered in this
   * document, and its full-page loader has not already run. Re-entering the same module while the
   * loader is still up (a self-redirect) keeps returning true, so the loader is not dropped midway.
   */
  claimColdLoad(moduleKey: string): boolean {
    if (this.coldLoadReleased.has(moduleKey)) {
      return false;
    }
    for (const rendered of this.modulesRendered) {
      if (rendered !== moduleKey) {
        return false;
      }
    }
    this.activeColdLoads.add(moduleKey);
    return true;
  }

  /** Call when the full-page loader finishes; later entries fall back to section skeletons. */
  releaseColdLoad(moduleKey: string): void {
    this.coldLoadReleased.add(moduleKey);
    this.activeColdLoads.delete(moduleKey);
    this.emitIfIdle();
  }

  private emitIfIdle(): void {
    if (!this.activeColdLoads.size) {
      this.coldLoadIdleSubject.next();
    }
  }

  /**
   * `/first-party/:id/collection/:sub` -> 'collection'; `/clients` -> 'clients'; `/login` -> null;
   * `/first-party/:id/collection/ai-tasks` and `/third-party/:id/ai-tasks` -> 'ai-tasks'.
   */
  private static moduleKeyOf(url: string): string | null {
    const segments = (url || '').split(/[?#]/)[0].split('/').filter(Boolean);
    if (!segments.length) {
      return null;
    }
    const scoped = segments[0] === 'first-party' || segments[0] === 'third-party' || segments[0] === 'vendor';
    const key = scoped ? segments[2] : segments[0];
    if (!key || NavigationContextService.NON_MODULE_ROUTES.includes(key)) {
      return null;
    }
    if (NavigationContextService.SCREEN_ROUTES.includes(key)) {
      return key;
    }
    // a screen nested one level under its module keeps its own key, so entering it is not read as
    // a second visit to that module (and vice versa)
    const nested = scoped ? segments[3] : segments[1];
    return NavigationContextService.SCREEN_ROUTES.includes(nested) ? nested : key;
  }
}
